import React, { useState, useEffect } from "react";
import "@/App.css";
import { 
  Video, 
  BookOpen, 
  Users, 
  MessageSquare, 
  ShieldCheck, 
  Sparkles, 
  TrendingUp, 
  DollarSign, 
  ArrowRight, 
  CheckCircle2, 
  Calculator, 
  HelpCircle, 
  Briefcase, 
  Copy, 
  Check, 
  Menu, 
  X, 
  Globe, 
  Percent, 
  Star,
  Award,
  Zap,
  Lock,
  ArrowUpRight,
  Info
} from "lucide-react";
import { Toaster, toast } from "sonner";

// Absolute Referral/Affiliate Signup Link using Creator ID
const REFERRAL_LINK = "https://dashboard.tagmango.com/?referredBy=66fc36108a531413111d43fd";
const AEMS_DOMAIN = "https://aemsmarketing.com";

export default function App() {
  // Navigation State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Calculator State
  const [students, setStudents] = useState(1500);
  const [ticketPrice, setTicketPrice] = useState(2500);

  // Lead Modal State
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", niche: "education", phone: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // FAQ Accordion State
  const [activeFaq, setActiveFaq] = useState(null);

  // Calculations for Creator Earnings
  // Total Revenue = Students * Ticket Price (Assuming annual or per workshop)
  const totalRevenue = students * ticketPrice;
  // TagMango takes standard 5% flat platform fee. Creator takes home 95%
  const tagmangoRevenue = totalRevenue * 0.95;
  const tagmangoFee = totalRevenue * 0.05;
  // Legacy platforms (e.g. Graphy/Teachable/Thinkific) typically charge upfront high fixed costs or 8% - 12% total cut
  const legacyRevenue = totalRevenue * 0.88; 
  const extraEarned = tagmangoRevenue - legacyRevenue;

  // Copy referral link handler
  const handleCopyLink = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(REFERRAL_LINK);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = REFERRAL_LINK;
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success("Affiliate Referral Link Copied!", {
        description: "Ready to share with other creators and earn commissions.",
        duration: 3000
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Copy failed. Please manually select the referral link.");
    }
  };

  // Lead Form Submission Handler
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) {
      toast.error("Please fill in your Name and Email Address.");
      return;
    }
    // Simulate API storage
    setFormSubmitted(true);
    toast.success("Exclusive Strategy Bonus Unlocked!", {
      description: "Our agency experts will email you your onboarding bonus shortly.",
      duration: 5000
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0A0D14] selection:bg-orange-500 selection:text-white">
      <Toaster position="bottom-right" richColors />

      {/* STICKY GLASSMORPHISM NAVBAR */}
      <nav 
        data-testid="navigation-bar" 
        className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/90 border-b border-orange-100/80 shadow-md transition-all duration-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Brand Logo & TagMango Partner Badge */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col text-left">
                <div className="relative leading-none">
                  <span className="font-['Great_Vibes'] text-3xl sm:text-4xl font-extrabold text-[#E27D18] tracking-wide select-none">
                    Aems <span className="text-[#0F172A] font-normal ml-0.5">Marketing</span>
                  </span>
                </div>
                <div className="text-[6.5px] sm:text-[7.5px] font-mono tracking-[0.14em] text-slate-500 uppercase font-extrabold leading-tight pt-0.5">
                  <div>Website Developers & Affiliate</div>
                  <div className="tracking-[0.3em]">Marketing</div>
                </div>
              </div>
              <div className="h-6 w-px bg-slate-200 hidden xl:block" />
              <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 px-2.5 py-0.5 rounded-full hidden xl:flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                <span className="text-[11px] font-semibold text-orange-600 font-mono">TagMango Partner</span>
              </div>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-8 font-semibold text-sm text-slate-700">
              <a 
                href="#features" 
                data-testid="nav-link-features"
                className="hover:text-orange-600 transition-colors duration-200"
              >
                Features
              </a>
              <a 
                href="#why-tagmango" 
                data-testid="nav-link-why-tagmango"
                className="hover:text-orange-600 transition-colors duration-200"
              >
                Why TagMango
              </a>
              <a 
                href="#earnings-calculator" 
                data-testid="nav-link-calculator"
                className="hover:text-orange-600 transition-colors duration-200"
              >
                Earnings Calculator
              </a>
              <a 
                href="#testimonials" 
                data-testid="nav-link-testimonials"
                className="hover:text-orange-600 transition-colors duration-200"
              >
                Success Stories
              </a>
              <a 
                href="#faq" 
                data-testid="nav-link-faq"
                className="hover:text-orange-600 transition-colors duration-200"
              >
                FAQ
              </a>
            </div>

            {/* Action Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button 
                onClick={handleCopyLink}
                data-testid="nav-copy-link-button"
                className="bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 hover:border-orange-500/40 text-xs font-semibold py-2 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 cursor-pointer shadow-sm"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                Copy Partner Link
              </button>
              <a 
                href={REFERRAL_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="nav-cta-button"
                className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-xs font-bold py-2.5 px-5 rounded-xl shadow-lg hover:shadow-orange-500/20 transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Claim Creator Link
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <div className="lg:hidden flex items-center gap-3">
              <button
                onClick={handleCopyLink}
                data-testid="mobile-copy-link-button"
                className="bg-white border border-slate-200 p-2 rounded-xl text-slate-700 hover:text-slate-900 shadow-sm"
                title="Copy Affiliate Link"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="text-slate-700 hover:text-slate-900 focus:outline-none p-1"
                aria-label="Toggle Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#FAF6EE] border-b border-orange-100 px-4 pt-3 pb-6 space-y-4 shadow-xl">
            <div className="flex flex-col space-y-3 font-semibold">
              <a 
                href="#features" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-orange-600 py-1 transition-colors duration-200"
              >
                Features
              </a>
              <a 
                href="#why-tagmango" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-orange-600 py-1 transition-colors duration-200"
              >
                Why TagMango
              </a>
              <a 
                href="#earnings-calculator" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-orange-600 py-1 transition-colors duration-200"
              >
                Earnings Calculator
              </a>
              <a 
                href="#testimonials" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-orange-600 py-1 transition-colors duration-200"
              >
                Success Stories
              </a>
              <a 
                href="#faq" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-orange-600 py-1 transition-colors duration-200"
              >
                FAQ
              </a>
            </div>
            <div className="pt-2 flex flex-col gap-3">
              <a 
                href={REFERRAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold py-3 px-4 rounded-xl text-center shadow-lg block"
              >
                Get Started on TagMango
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* HIGH-IMPACT HERO SECTION */}
      <section 
        data-testid="hero-section" 
        className="relative overflow-hidden pt-12 pb-24 sm:pt-20 sm:pb-32 lg:pb-40"
      >
        {/* Glow backdrop overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,87,34,0.06),rgba(253,251,247,0))] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Copy Column */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20 uppercase tracking-widest animate-pulse">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Exclusive Referral Partnership
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-slate-900">
                Turn Your Audience Into A <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-amber-600 to-yellow-600">High-Yield</span> Creator Empire
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-slate-700 leading-relaxed max-w-2xl">
                Tired of losing 15%+ of your revenue to tech headaches and legacy course builders? Build, scale, and host your entire digital business on <strong>TagMango</strong>—the highest-converting, zero-code hub for modern creators. 
              </p>

              {/* Core value bullets */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {[
                  "Host Courses & Live Cohorts",
                  "Native Android & iOS Apps",
                  "Automated WhatsApp & Email Marketing",
                  "Ultra-Fast Global Payouts & 0% Tech Work"
                ].map((bullet, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-800">{bullet}</span>
                  </div>
                ))}
              </div>

              {/* Big CTA buttons row */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
                <a 
                  href={REFERRAL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-signup-cta"
                  className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-extrabold py-4 px-8 rounded-xl shadow-xl hover:shadow-orange-500/25 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 text-center flex items-center justify-center gap-3 cursor-pointer"
                >
                  Start Your Creator Free Trial
                  <ArrowRight className="w-5 h-5" />
                </a>

                <button 
                  onClick={() => setShowLeadModal(true)}
                  data-testid="hero-copy-referral-cta"
                  className="bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 hover:border-slate-300 font-bold py-4 px-6 rounded-xl transition-all duration-200 text-center flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  <Briefcase className="w-4 h-4 text-orange-500" />
                  Claim Free Onboarding Bonus
                </button>
              </div>

              {/* Bonus Tag */}
              <div className="text-xs text-slate-700 bg-[#FAF6EE] border border-orange-100 rounded-xl p-3 max-w-xl flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-600 shrink-0" />
                <span>
                  <strong>AEMS Agency Exclusive:</strong> Sign up through our link to get a <strong>1-on-1 Strategy Setup Call + Custom Funnel Review</strong> entirely free.
                </span>
              </div>
            </div>

            {/* Right Graphic/Metric Column - Interactive Video Player & Supporting Images */}
            <div className="lg:col-span-5 relative mt-8 lg:mt-0">
              <div className="relative mx-auto max-w-[480px] lg:max-w-none space-y-6">
                
                {/* Decorative background warmth shadow */}
                <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/10 to-amber-500/5 rounded-3xl blur-2xl transform scale-105 pointer-events-none" />
                
                {/* 1. Official Explainer Video Embed */}
                <div className="relative rounded-3xl bg-white border border-slate-200/80 p-3 shadow-2xl overflow-hidden">
                  <div className="relative rounded-2xl overflow-hidden aspect-video border border-slate-100 bg-slate-950">
                    <iframe 
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/jFEbJYyNC8s" 
                      title="TagMango Explainer Video" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                      data-testid="hero-explainer-video"
                    />
                  </div>
                  <div className="p-3 text-left">
                    <div className="text-xs uppercase tracking-widest font-bold text-orange-600 font-mono flex items-center gap-1.5">
                      <Video className="w-3.5 h-3.5 animate-pulse" /> Official Explainer Walkthrough
                    </div>
                    <p className="text-xs text-slate-700 font-semibold mt-1">See how TagMango replaces Teachable, Graphy, Zoom, and WhatsApp in 5 minutes</p>
                  </div>
                </div>

                {/* 2. Supporting Credentials & Benefits Images (Side-by-Side Grid) */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Image 1: Interactive Onboarding workshops */}
                  <div className="bg-white border border-slate-200/80 p-2 rounded-2xl shadow-md space-y-2 group overflow-hidden">
                    <div className="rounded-xl overflow-hidden h-24 sm:h-28 border border-slate-100 bg-slate-50">
                      <img 
                        src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=400&q=80" 
                        alt="Onboarding workshops benefit credentials" 
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                        data-testid="hero-supporting-img-1"
                      />
                    </div>
                    <div className="text-left px-1">
                      <h4 className="text-xs font-extrabold text-slate-900 tracking-tight">Interactive Live Cohorts</h4>
                      <p className="text-[10px] text-slate-500 font-medium mt-0.5">Scale classrooms instantly</p>
                    </div>
                  </div>

                  {/* Image 2: Creator Revenue Dashboard analytics */}
                  <div className="bg-white border border-slate-200/80 p-2 rounded-2xl shadow-md space-y-2 group overflow-hidden">
                    <div className="rounded-xl overflow-hidden h-24 sm:h-28 border border-slate-100 bg-slate-50">
                      <img 
                        src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=400&q=80" 
                        alt="Ecosystem analytics dashboard credentials" 
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                        data-testid="hero-supporting-img-2"
                      />
                    </div>
                    <div className="text-left px-1">
                      <h4 className="text-xs font-extrabold text-slate-900 tracking-tight">Revenue Analytics</h4>
                      <p className="text-[10px] text-slate-500 font-medium mt-0.5">Instant automated payments</p>
                    </div>
                  </div>
                </div>

                {/* Overlaid Badge */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3.5 rounded-2xl shadow-xl flex items-center gap-3 text-left">
                  <div className="bg-white p-1.5 rounded-xl shrink-0">
                    <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase font-mono tracking-widest text-slate-100 font-bold">Industry Certified</div>
                    <div className="text-xs sm:text-sm font-extrabold text-white">Flat 5% Platform Fee — High-Yield Earnings</div>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* METRICS TICKER ROW */}
          <div className="border-t border-slate-200 mt-20 pt-10 sm:mt-24 sm:pt-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left">
              {[
                { val: "₹2.5 Billion+", label: "Creator Earnings Paid" },
                { val: "10,000+", label: "Global Active Creators" },
                { val: "0% Flat Code", label: "Tech Overhead Required" },
                { val: "95% Payout", label: "Industry Best Take-Home" }
              ].map((metric, index) => (
                <div key={index} className="space-y-1.5">
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{metric.val}</div>
                  <div className="text-xs sm:text-sm text-slate-600 font-semibold">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CORE FEATURE GRID (BENTO GRID STYLE) */}
      <section 
        id="features" 
        className="py-20 sm:py-32 bg-[#FAF6EE] border-t border-orange-100 relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-left space-y-4 mb-16 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20">
              EXPLORE THE FEATURES
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
              One Unified Ecosystem. Endless Revenue Outlets.
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Why piece together a dozen disconnected tools? TagMango aggregates payments, course hosting, notifications, analytics, and marketing into a single, high-performance creator engine.
            </p>
          </div>

          {/* Bento Grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 lg:gap-8">
            
            {/* Feature 1: Courses (Bento Large) */}
            <div 
              data-testid="feature-card-courses"
              className="md:col-span-3 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md overflow-hidden p-6 sm:p-8 flex flex-col justify-between group"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-500">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Structured Courses & cohorts</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Host pre-recorded courses, drip-feed chapters, attach homework, manage assignments, and execute live cohorts seamlessly. Secured with military-grade DRM to block piracy.
                </p>
              </div>
              <div className="mt-8 overflow-hidden rounded-xl border border-orange-100">
                <img 
                  src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=800&q=80" 
                  alt="Courses dashboard preview" 
                  className="w-full h-40 object-cover object-center group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
            </div>

            {/* Feature 2: Workshops (Bento Small) */}
            <div 
              data-testid="feature-card-workshops"
              className="md:col-span-3 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md overflow-hidden p-6 sm:p-8 flex flex-col justify-between group"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
                  <Video className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">One-Click Live Workshops</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Integrate instantly with Zoom or TagMango’s custom video engine. Sell access tickets, collect fast registrations, send calendar schedules, and trigger reminders automatically.
                </p>
              </div>
              <div className="mt-8 overflow-hidden rounded-xl border border-orange-100">
                <img 
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&q=80" 
                  alt="Live workshops cohort preview" 
                  className="w-full h-40 object-cover object-center group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
            </div>

            {/* Feature 3: Subscriptions (Bento Wide) */}
            <div 
              data-testid="feature-card-subscriptions"
              className="md:col-span-4 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-center justify-between group"
            >
              <div className="space-y-4 max-w-lg text-left">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Recurring Subscriptions (Mangoes)</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Create tiered subscription products (referred as 'Mangoes') that unlock recurring revenue streams. Offer member-only content feeds, exclusive communities, webinars, and group coaching.
                </p>
              </div>
              <div className="w-full sm:w-64 overflow-hidden rounded-xl border border-orange-100 shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80" 
                  alt="Subscription revenue charts" 
                  className="w-full h-32 sm:h-40 object-cover object-center group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
            </div>

            {/* Feature 4: Chats & Community (Bento Compact) */}
            <div 
              data-testid="feature-card-chats"
              className="md:col-span-2 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md p-6 sm:p-8 flex flex-col justify-between group"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-500">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">In-App Chats & Feeds</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Engage subscribers in dedicated topic channels. Block noise, share polls, trade resources, and spark live group messaging.
                </p>
              </div>
            </div>

            {/* Feature 5: AI Suite (Bento Compact) */}
            <div 
              data-testid="feature-card-ai"
              className="md:col-span-2 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md p-6 sm:p-8 flex flex-col justify-between group"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">Mango AI Assistant</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Draft sales copy, auto-generate email marketing funnels, compile course outlines, and run chatbot support instantly.
                </p>
              </div>
            </div>

            {/* Feature 6: Invoicing & Security (Bento Wide) */}
            <div 
              data-testid="feature-card-invoicing"
              className="md:col-span-4 rounded-2xl bg-white border border-orange-100 hover:border-orange-500/40 transition-all duration-300 shadow-md p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-center justify-between group"
            >
              <div className="space-y-4 max-w-lg text-left">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Secure Global Invoicing</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Enjoy absolute localized taxation compliance. Auto-generate custom professional invoices, handle GST, collect cards globally, and deliver fast, secure payouts without accounting stress.
                </p>
              </div>
              <div className="w-full sm:w-64 overflow-hidden rounded-xl border border-orange-100 shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=600&q=80" 
                  alt="Secure transactions dashboard" 
                  className="w-full h-32 sm:h-40 object-cover object-center group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
            </div>

          </div>

          {/* Secondary Features Banner CTA */}
          <div className="mt-16 bg-white border border-orange-100 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row justify-between items-center gap-6 shadow-md">
            <div className="flex items-center gap-4 text-left">
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 shrink-0">
                <Info className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-slate-900">White-Label Native Mobile Apps</h4>
                <p className="text-xs sm:text-sm text-slate-600 mt-0.5">Want your own app published on the Apple App Store and Google Play Store? TagMango handles development and release.</p>
              </div>
            </div>
            <a 
              href={REFERRAL_LINK} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-6 rounded-xl text-sm transition-colors text-center shrink-0 w-full md:w-auto"
            >
              Get Custom App Pricing
            </a>
          </div>

        </div>
      </section>

      {/* WHY TAGMANGO VS LEGACY PLATFORMS */}
      <section 
        id="why-tagmango" 
        className="py-20 sm:py-32 bg-[#FDFBF7] border-t border-orange-100 relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center space-y-4 mb-20 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20 uppercase">
              THE ULTIMATE COMPARISON
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
              Why Top Creators Migrate to TagMango
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Stop bleeding margin and wasting hours troubleshooting integrations. See how TagMango stacks up against legacy LMS software and general webinar portals.
            </p>
          </div>

          {/* Comparison Matrix Table */}
          <div className="w-full overflow-hidden px-1 py-4">
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <table className="w-full text-left border-collapse min-w-[750px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 font-mono text-xs uppercase tracking-wider">
                  <th className="p-6 font-bold text-slate-600">Features & Economics</th>
                  <th className="p-6 font-bold text-orange-600 text-center border-x border-orange-100 bg-orange-50/40">TagMango (Partner Referral)</th>
                  <th className="p-6 font-bold text-slate-600 text-center">Legacy LMS (e.g. Graphy, Teachable)</th>
                  <th className="p-6 font-bold text-slate-600 text-center">Zoom + WhatsApp Manual Setup</th>
                </tr>
              </thead>
              <tbody className="text-sm font-semibold divide-y divide-slate-200">
                {[
                  {
                    title: "Platform Take-Home Cut",
                    tm: "Flat 5% Platform Fee (Best Margin)",
                    legacy: "8% to 15% OR $119+/mo fixed",
                    manual: "Varies (Stripe fees + software costs)"
                  },
                  {
                    title: "WhatsApp Marketing Integrations",
                    tm: "100% Free & Built-In (Auto trigger reminders)",
                    legacy: "Needs expensive 3rd party plugins",
                    manual: "Manual copying/messaging"
                  },
                  {
                    title: "White-Label App Development",
                    tm: "Done-for-you publish to App/Play Stores",
                    legacy: "Unavailable or expensive enterprise option",
                    manual: "Completely impossible"
                  },
                  {
                    title: "Piracy Defense & Video Hosting",
                    tm: "Military-grade DRM screen record block",
                    legacy: "Requires premium Vimeo ($50/mo)",
                    manual: "High risk (Shared drive links leak)"
                  },
                  {
                    title: "International Payments & GST Invoice",
                    tm: "Automatic global cards, auto invoices",
                    legacy: "Manual configuration / tax setup",
                    manual: "Requires third-party billing software"
                  },
                  {
                    title: "1-on-1 Agency Launch Bonus Support",
                    tm: "AEMS Free Strategy + Custom Setup Audit",
                    legacy: "No launch strategy or direct consulting",
                    manual: "Zero platform support"
                  }
                ].map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-6 font-bold text-slate-900 text-left">{row.title}</td>
                    <td className="p-6 text-center text-orange-600 bg-orange-50/40 font-bold border-x border-orange-100">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>{row.tm}</span>
                      </div>
                    </td>
                    <td className="p-6 text-slate-700 text-center font-normal">{row.legacy}</td>
                    <td className="p-6 text-slate-700 text-center font-normal">{row.manual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>

          {/* Quick Quote Alert */}
          <div className="mt-8 text-center text-xs sm:text-sm text-slate-600 flex items-center justify-center gap-2 max-w-2xl mx-auto font-mono">
            <Info className="w-4 h-4 text-orange-500" />
            <span>Ready to migrate your audience? TagMango offers <strong>100% free migration assistance</strong> from Teachable, Graphy, and Kajabi.</span>
          </div>

        </div>
      </section>

      {/* INTERACTIVE EARNINGS & COMMISSION CALCULATOR */}
      <section 
        id="earnings-calculator" 
        className="py-20 sm:py-32 bg-[#FAF6EE] border-t border-orange-100 relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-5 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20 uppercase">
                Interactive Earnings Predictor
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
                Calculate Your Real-Time Net Earnings
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                Configure your expected active student count and average course/ticket price. See how TagMango's flat 5% platform fee keeps significantly more capital in your business compared to traditional platforms.
              </p>

              {/* Formula explanation list */}
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 text-xs font-black shrink-0 mt-0.5">1</div>
                  <span className="text-xs sm:text-sm text-slate-700">
                    <strong className="text-slate-900">95% Paytake:</strong> You keep 95% of every transaction. Flat.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 text-xs font-black shrink-0 mt-0.5">2</div>
                  <span className="text-xs sm:text-sm text-slate-700">
                    <strong className="text-slate-900">Zero Monthly Subscriptions:</strong> No upfront monthly plans needed to start.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 text-xs font-black shrink-0 mt-0.5">3</div>
                  <span className="text-xs sm:text-sm text-slate-700">
                    <strong className="text-slate-900">AEMS Onboarding Package:</strong> Sign up through us and secure free agency audits to launch.
                  </span>
                </div>
              </div>
            </div>

            {/* Calculator Interface Column */}
            <div className="lg:col-span-7">
              <div 
                data-testid="calculator-card"
                className="bg-white border border-orange-100 p-6 sm:p-10 rounded-3xl shadow-xl relative overflow-hidden"
              >
                {/* Visual Header */}
                <div className="flex justify-between items-center border-b border-orange-50 pb-5 mb-8">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-orange-500 animate-bounce" />
                    <span className="text-sm font-bold text-slate-800 uppercase tracking-wider font-mono">Commission Calculator</span>
                  </div>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-mono font-bold px-2.5 py-1 rounded-full uppercase">
                    Flat 5% platform fee
                  </span>
                </div>

                <div className="space-y-8">
                  
                  {/* Slider 1: Students */}
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="font-bold text-slate-700">Total Expected Students / Buyers</span>
                      <span className="font-black text-slate-900 font-mono text-lg bg-slate-50 px-3 py-1 rounded-lg border border-slate-200">
                        {students.toLocaleString()}
                      </span>
                    </div>
                    <input 
                      type="range" 
                      min="100" 
                      max="10000" 
                      step="100"
                      value={students} 
                      onChange={(e) => setStudents(Number(e.target.value))}
                      data-testid="calc-slider-students"
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 font-semibold font-mono">
                      <span>100 Students</span>
                      <span>5,000</span>
                      <span>10,000 Students</span>
                    </div>
                  </div>

                  {/* Slider 2: Ticket Price */}
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="font-bold text-slate-700">Average Course / Ticket Price (₹)</span>
                      <span className="font-black text-orange-600 font-mono text-lg bg-orange-50/50 px-3 py-1 rounded-lg border border-orange-150">
                        ₹{ticketPrice.toLocaleString()}
                      </span>
                    </div>
                    <input 
                      type="range" 
                      min="500" 
                      max="50000" 
                      step="500"
                      value={ticketPrice} 
                      onChange={(e) => setTicketPrice(Number(e.target.value))}
                      data-testid="calc-slider-price"
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 font-semibold font-mono">
                      <span>₹500</span>
                      <span>₹25,000</span>
                      <span>₹50,000</span>
                    </div>
                  </div>

                  {/* Results Breakdown Block */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                    
                    {/* Gross Revenue */}
                    <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800/60">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold">Gross Audience Revenue</span>
                      <div className="text-xl sm:text-2xl font-black text-white mt-1 font-mono">
                        ₹{totalRevenue.toLocaleString()}
                      </div>
                    </div>

                    {/* Net TagMango Creator Revenue */}
                    <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/5 p-4 rounded-2xl border border-orange-500/20">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-orange-400 font-bold">Net TagMango Payout</span>
                      <div className="text-xl sm:text-2xl font-black text-orange-400 mt-1 font-mono" data-testid="calc-estimated-revenue">
                        ₹{tagmangoRevenue.toLocaleString()}
                      </div>
                    </div>

                  </div>

                  {/* Profit Comparison & Extra Cash */}
                  <div className="bg-slate-950 border border-slate-800/80 p-5 rounded-2xl space-y-4">
                    <div className="flex justify-between items-center text-xs sm:text-sm">
                      <span className="text-slate-400">Estimated legacy platform payout (12% cut):</span>
                      <span className="text-slate-300 font-mono font-bold">₹{legacyRevenue.toLocaleString()}</span>
                    </div>

                    <div className="flex justify-between items-center text-xs sm:text-sm pt-2 border-t border-slate-800/40">
                      <span className="text-slate-400 flex items-center gap-1.5 font-semibold">
                        <Percent className="w-4 h-4 text-emerald-400" />
                        AEMS Referral Margin Advantage:
                      </span>
                      <span className="text-emerald-400 font-mono font-black text-base" data-testid="calc-affiliate-earnings">
                        +₹{extraEarned.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Copy Affiliate link and Signup Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <button 
                      onClick={handleCopyLink}
                      data-testid="calc-copy-link-btn"
                      className="bg-slate-950 hover:bg-slate-800 text-slate-200 border border-slate-800 hover:border-orange-500/40 font-bold py-3.5 px-5 rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer flex-1"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Link Copied!" : "Copy Affiliate Link"}
                    </button>

                    <a 
                      href={REFERRAL_LINK} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-extrabold py-3.5 px-6 rounded-xl text-sm shadow-lg text-center flex items-center justify-center gap-2 flex-1 hover:shadow-orange-500/20 transition-all duration-300 transform hover:-translate-y-0.5"
                    >
                      Sign Up On TagMango
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>

                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* SOCIAL PROOF: CREATOR SUCCESS STORIES */}
      <section 
        id="testimonials" 
        className="py-20 sm:py-32 bg-[#FDFBF7] border-t border-orange-100 relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center space-y-4 mb-20 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20 uppercase">
              CREATOR REVIEWS & BADGES
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
              Validated by Thousands of Creators
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Educators, business coaches, content agencies, and skill instructors have built highly profitable empires on TagMango. Read their actual experience.
            </p>
          </div>

          {/* Testimonial Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Ananya Mehta",
                role: "Digital Marketing Coach & Trainer",
                img: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80",
                revenue: "₹1.5M/month cohort revenue",
                quote: "Before migrating, my team was wasting hours copy-pasting Zoom links and setting up payment gateway invoices manually. TagMango automated 100% of this, boosting our checkout conversion by 28% instantly. Highly recommended!"
              },
              {
                name: "Rahul Deshmukh",
                role: "Trading & Investment Educator",
                img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
                revenue: "₹4.2M workshop sales",
                quote: "Piracy was a massive threat to my proprietary trading courses. TagMango's DRM video protection completely blocks unauthorized screen recording, saving me millions in stolen content. The localized payment split is super fast."
              },
              {
                name: "Siddharth Sen",
                role: "Content Agency Founder & Consultant",
                img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80",
                revenue: "2,500+ active subscribers",
                quote: "The recurring subscriptions and community feed features are incredibly engaging. My subscribers get automatic updates on WhatsApp and can chat in specialized channels right inside the native white-label iOS app."
              }
            ].map((t, idx) => (
              <div 
                key={idx} 
                data-testid={`testimonial-card-${idx+1}`}
                className="bg-white border border-slate-200/60 hover:border-orange-500/20 rounded-2xl p-6 sm:p-8 flex flex-col justify-between shadow-md transition-all duration-300"
              >
                <div className="space-y-4">
                  {/* Rating Stars */}
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed italic text-left">
                    "{t.quote}"
                  </p>
                </div>

                <div className="flex items-center gap-4 mt-8 pt-6 border-t border-slate-100">
                  <img 
                    src={t.img} 
                    alt={t.name} 
                    className="w-12 h-12 rounded-full object-cover object-center shrink-0 border border-slate-200"
                  />
                  <div className="text-left">
                    <h4 className="text-sm sm:text-base font-bold text-slate-900">{t.name}</h4>
                    <p className="text-xs text-slate-600">{t.role}</p>
                    <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-mono font-bold px-2 py-0.5 rounded mt-1.5">
                      {t.revenue}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (FAQ) ACCORDION */}
      <section 
        id="faq" 
        className="py-20 sm:py-32 bg-[#FAF6EE] border-t border-orange-100 relative"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center space-y-4 mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20 uppercase">
              HAVE ANY CONCERNS?
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Clear, transparent answers about the TagMango platform, commissions, migration support, and the exclusive AEMS agency launch bonus.
            </p>
          </div>

          {/* Self-contained Custom Accordion List */}
          <div className="space-y-4">
            {[
              {
                q: "What is TagMango and how does the affiliate signup work?",
                a: "TagMango is an all-in-one monetisation suite for creators to host courses, workshops, subscriptions, and communities. By signing up via the affiliate referral buttons on this page, you connect with AEMS Marketing's exclusive launch support which entitles you to free consultation credits and funnel auditing, with no added platform cost."
              },
              {
                q: "How does the flat 5% platform fee compare to Teachable or Graphy?",
                a: "Most platforms charge massive upfront subscription plans (often $119+/month) plus transaction processing cuts, or grab up to 10% - 15% of your gross sales. TagMango requires NO upfront monthly fees to launch and takes only a flat 5% on successful transactions, leaving 95% of your earnings straight in your pocket."
              },
              {
                q: "Can I publish my own custom white-labeled mobile apps on the App Store?",
                a: "Yes! TagMango specializes in compiling and publishing fully white-labeled native iOS and Android apps under your own brand name and developer accounts. Your students can watch DRM-secured courses directly from their mobile phones."
              },
              {
                q: "What does AEMS Marketing provide as an onboarding bonus?",
                a: "As official TagMango builder partners, when you sign up using our referral link, you gain access to our custom 1-on-1 strategy onboarding setup call, a technical walkthrough, and a custom marketing funnel audit (worth over ₹15,000) entirely free of charge to accelerate your initial launch."
              },
              {
                q: "Does TagMango support automatic localized taxation and invoicing?",
                a: "Absolutely. The platform auto-generates localized professional invoices containing correct GST/tax parameters for your buyers. Global cards, Netbanking, UPI, and international credit systems are processed out-of-the-box."
              }
            ].map((item, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <div 
                  key={idx}
                  className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : idx)}
                    data-testid={`faq-accordion-trigger-${idx+1}`}
                    className="w-full p-6 text-left flex items-center justify-between font-bold text-sm sm:text-base text-slate-950 hover:text-orange-600 transition-colors duration-200 focus:outline-none"
                  >
                    <span>{item.q}</span>
                    <HelpCircle className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-orange-500' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4 text-left">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* CONVERSION SURGE CTA BANNER (Sticky Energy Section) */}
      <section className="relative overflow-hidden py-16 sm:py-24 bg-gradient-to-r from-orange-600 to-amber-700">
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono font-bold bg-slate-950/20 text-white border border-white/20">
            LIMITED OPPORTUNITY • LAUNCH BONUS VALID TODAY
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white max-w-4xl mx-auto">
            Ready to Build Your Creator Empire?
          </h2>

          <p className="text-sm sm:text-base lg:text-lg text-orange-100 max-w-2xl mx-auto leading-relaxed">
            Stop letting tech bottlenecks hold your business back. Claim your official TagMango referral signup now and unlock your free AEMS strategy package.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a 
              href={REFERRAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bottom-signup-cta"
              className="bg-slate-950 hover:bg-slate-900 text-white font-black py-4 px-8 rounded-xl shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 w-full sm:w-auto text-center"
            >
              Sign Up via Partner Link
            </a>
            <button 
              onClick={() => setShowLeadModal(true)}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/25 font-bold py-4 px-8 rounded-xl transition-all duration-200 w-full sm:w-auto text-center cursor-pointer"
            >
              Lock In Onboarding Bonus
            </button>
          </div>

          {/* Safe lock disclosure */}
          <div className="text-xs text-orange-200 font-medium max-w-md mx-auto flex items-center justify-center gap-1.5">
            <Lock className="w-3.5 h-3.5" />
            <span>Secure official checkout & login powered directly by TagMango</span>
          </div>
        </div>
      </section>

      {/* FOOTER & DISCLOSURES */}
      <footer className="bg-slate-950 border-t border-slate-900 py-12 sm:py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            
            {/* Column 1: Brand & Logo */}
            <div className="md:col-span-5 space-y-4 text-left">
              <div className="flex flex-col text-left">
                <div className="relative leading-none">
                  <span className="font-['Great_Vibes'] text-3xl sm:text-4xl font-extrabold text-[#E27D18] tracking-wide select-none">
                    Aems <span className="text-white font-normal ml-0.5">Marketing</span>
                  </span>
                </div>
                <div className="text-[6.5px] sm:text-[7.5px] font-mono tracking-[0.14em] text-slate-400 uppercase font-extrabold leading-tight pt-0.5">
                  <div>Website Developers & Affiliate</div>
                  <div className="tracking-[0.3em]">Marketing</div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 max-w-sm leading-relaxed pt-2">
                AEMS Marketing is an official digital agency and content builder partner. We specialize in building custom high-conversion funnels, coaching platforms, and white-label applications.
              </p>
              <div className="text-xs text-slate-500 font-mono">
                Canonical: <a href={AEMS_DOMAIN} data-testid="footer-brand-link" className="hover:text-orange-400 underline">{AEMS_DOMAIN}</a>
              </div>
            </div>

            {/* Column 2: Quick Navigation */}
            <div className="md:col-span-3 space-y-4 text-left">
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-300 font-bold">Portal Quick Links</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-400 font-medium">
                <li><a href="#features" className="hover:text-orange-500 transition-colors">Platform Features</a></li>
                <li><a href="#why-tagmango" className="hover:text-orange-500 transition-colors">Comparison Analysis</a></li>
                <li><a href="#earnings-calculator" className="hover:text-orange-500 transition-colors">Earnings Calculator</a></li>
                <li><a href="#testimonials" className="hover:text-orange-500 transition-colors">Success Testimonials</a></li>
              </ul>
            </div>

            {/* Column 3: Contact & Legal */}
            <div className="md:col-span-4 space-y-4 text-left">
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-300 font-bold">Inquiries & Onboarding</h4>
              <p className="text-xs sm:text-sm text-slate-400">
                Want to hire AEMS Marketing to design or manage your entire TagMango funnel ecosystem? Reach out directly:
              </p>
              <div className="space-y-2 pt-2">
                <div className="text-xs text-slate-300 font-semibold bg-slate-900 border border-slate-800 p-3 rounded-lg flex flex-col gap-1">
                  <div className="font-extrabold text-slate-100 font-mono text-[10px] uppercase tracking-wider">AEMS Headquarters</div>
                  <div className="text-slate-400">Mahim West, Mumbai 400016</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <a 
                    href="https://wa.me/918291712551" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 font-bold transition-all text-center block"
                  >
                    WhatsApp Chat
                  </a>
                  <a 
                    href="mailto:ceo@aemsmarketing.com"
                    className="p-2.5 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-400 font-bold transition-all text-center block"
                  >
                    Send Email
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Legal FTC disclosure block */}
          <div className="border-t border-slate-900 pt-8 mt-8 space-y-4 text-left text-[11px] text-slate-500 leading-relaxed max-w-5xl">
            <p>
              <strong>Official Affiliate Disclaimer:</strong> This single-page creator referral platform is owned and operated by AEMS Marketing, an official registered affiliate partner of TagMango. Clicking the referral sign-up or checkout buttons on this website redirects you to TagMango's official servers via tracking codes. AEMS Marketing may earn financial referral commissions on subsequent qualifying customer transactions or platform accounts created, at absolutely no additional cost to you.
            </p>
            <p>
              All trademarks, product names, logos, branding layouts, and assets associated with TagMango (such as Mango, Mango Studio, DRM course hosting) are property of TagMango. We offer specialized consulting, funnel coaching, and white-label design setups independently as agency specialists.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 border-t border-slate-900 pt-6 text-[10px] sm:text-xs">
              <span>© {new Date().getFullYear()} AEMS Marketing. All Rights Reserved.</span>
              <div className="flex gap-4">
                <a href="#privacy" className="hover:underline">Privacy Policy</a>
                <span>•</span>
                <a href="#terms" className="hover:underline">Terms of Service</a>
                <span>•</span>
                <a href={REFERRAL_LINK} data-testid="footer-affiliate-link" className="hover:underline text-orange-400 font-semibold">TagMango Affiliate Link</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* LEAD COLLECTION & STRATEGY BONUS MODAL */}
      {showLeadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="relative bg-[#FDFBF7] border border-orange-100 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-6 text-left">
            
            <button
              onClick={() => {
                setShowLeadModal(false);
                setFormSubmitted(false);
              }}
              className="absolute top-4 right-4 text-slate-500 hover:text-[#0A0D14]"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            {!formSubmitted ? (
              <form onSubmit={handleLeadSubmit} className="space-y-5">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20">
                    AEMS AGENCY EXCLUSIVE
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-slate-900">Unlock Free Launch Setup</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Submit your details and sign up on TagMango using our referral link. We will provide a <strong>Free Funnel Audit + Strategy walkthrough Call</strong> (worth over ₹15,000) for your launch!
                  </p>
                </div>

                <div className="space-y-3.5">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 font-mono uppercase tracking-wider">Your Full Name</label>
                    <input 
                      type="text"
                      required
                      placeholder="Jane Doe"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      data-testid="lead-input-name"
                      className="w-full bg-white border border-slate-200 focus:border-orange-500 p-3 rounded-xl text-sm text-slate-950 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 font-mono uppercase tracking-wider">Your Email Address</label>
                    <input 
                      type="email"
                      required
                      placeholder="jane@example.com"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      data-testid="lead-input-email"
                      className="w-full bg-white border border-slate-200 focus:border-orange-500 p-3 rounded-xl text-sm text-slate-950 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 font-mono uppercase tracking-wider">Course / Cohort Niche</label>
                    <select
                      value={leadForm.niche}
                      onChange={(e) => setLeadForm({ ...leadForm, niche: e.target.value })}
                      className="w-full bg-white border border-slate-200 focus:border-orange-500 p-3 rounded-xl text-sm text-slate-950 focus:outline-none transition-colors"
                    >
                      <option value="education">Education & Academics</option>
                      <option value="trading">Stock Trading & Finance</option>
                      <option value="marketing">Digital Marketing & Agency</option>
                      <option value="fitness">Fitness & Wellness</option>
                      <option value="other">Other Creative Skills</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="lead-submit-button"
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-extrabold py-3.5 rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2 cursor-pointer text-sm"
                >
                  Lock In My Setup Bonus
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <div className="space-y-5 text-center py-4">
                <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl sm:text-2xl font-black text-slate-900">Bonus Locked In!</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Amazing, <strong>{leadForm.name}</strong>! We've registered your interest for the AEMS Creator Package.
                  </p>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed">
                  Now, the critical next step is to create your free platform account on TagMango. Tap the button below to register under our partner program and activate your perks:
                </p>

                <div className="space-y-3 pt-2">
                  <a
                    href={REFERRAL_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white font-black py-4 px-6 rounded-xl shadow-lg block text-sm text-center"
                  >
                    Create My TagMango Account Now
                  </a>
                  
                  <button
                    onClick={handleCopyLink}
                    className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 font-bold py-3 px-4 rounded-xl text-xs transition-colors"
                  >
                    Copy Referral URL Instead
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
